import React, { useState, useEffect } from 'react';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import dashboardAPI from '../../../services/dashboardService';
import { Stethoscope, Activity, CalendarCheck } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
    </div>
);

const DetailedMedicalPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const filters = externalFilters || localFilters;
    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    const fetchStats = async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getMedicalStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error("Failed to fetch medical stats", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats(filters);
    }, [filters]);

    if (loading || !stats) {
        return <div className="p-8 text-center text-gray-500">Loading statistics...</div>;
    }

    const statusData = stats.appointmentsByStatus ? Object.entries(stats.appointmentsByStatus).map(([key, value]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        value: value
    })) : [];

    const graphData = stats.graph_data || [];

    return (
        <DetailedPageLayout
            title="Medical Analytics"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            {/* Top Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                <StatCard
                    title="Total Appointments"
                    value={stats.totalAppointments}
                    icon={CalendarCheck}
                    color="bg-red-500"
                />
                <StatCard
                    title="Active Clinics"
                    value={stats.activeClinics}
                    icon={Stethoscope}
                    color="bg-teal-500"
                />
                <StatCard
                    title="Completion Rate"
                    value={`${stats.completionRate}%`}
                    icon={Activity}
                    color="bg-orange-500"
                />
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CustomBarChart
                    data={graphData}
                    title="Appointment Volume"
                />
                <CustomDonutChart
                    data={statusData}
                    totalValue={stats.totalAppointments}
                    title="Appointment Status"
                />
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 gap-6">
                <CustomAreaChart
                    data={graphData}
                    color="#ef4444"
                    title="Appointments Over Time"
                />
            </div>
        </DetailedPageLayout>
    );
};

export default DetailedMedicalPage;
